import React, { useEffect, useState } from 'react';
import { meetingService } from '../../services/meetingService';
import { formatFirstSessionDate } from '../../services/workshop2026Service';
import {
  formatCommunityCardSubtitle,
  formatCommunityCardTitle,
  formatConfirmedRosterNames,
  formatInterestListSummary,
  formatInterestedViewerLines,
  WorkshopCommunityViewerRole,
} from '../../utils/workshopRosterText';
import { Meeting } from '../../services/meetingService';
import styles from '../../styles/workshop2026Page.module.css';

export interface WorkshopCommunityHeader {
  title: string;
  subtitle?: string;
}

interface WorkshopCommunitySectionProps {
  meetingCode: string;
  meeting: Meeting | null;
  registrationState: 'interest' | 'confirmed';
  onHeaderChange?: (header: WorkshopCommunityHeader) => void;
}

const WorkshopCommunitySection: React.FC<WorkshopCommunitySectionProps> = ({
  meetingCode,
  meeting,
  registrationState,
  onHeaderChange,
}) => {
  const [rosterNames, setRosterNames] = useState<string[]>([]);
  const [namesHiddenCount, setNamesHiddenCount] = useState(0);
  const [confirmedCount, setConfirmedCount] = useState(0);
  const [interestCount, setInterestCount] = useState(0);
  const [workshopStarted, setWorkshopStarted] = useState(false);
  const [viewerShowsName, setViewerShowsName] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const firstSession = formatFirstSessionDate(meeting);
  const viewerRole: WorkshopCommunityViewerRole =
    registrationState === 'confirmed' ? 'confirmed' : 'interest';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const roster = await meetingService.getWorkshopRoster(meetingCode);
        if (cancelled) return;
        const names = roster.names.map((n) => n.display_name).filter(Boolean);
        setRosterNames(names);
        setNamesHiddenCount(roster.names_hidden_count ?? 0);
        setConfirmedCount(roster.confirmed_count ?? 0);
        setInterestCount(roster.interest_count ?? 0);
        setWorkshopStarted(roster.workshop_started);
        setViewerShowsName(roster.viewer_shows_name !== false);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not load participant info');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [meetingCode]);

  useEffect(() => {
    if (loading || error) return;
    onHeaderChange?.({
      title: formatCommunityCardTitle(
        viewerRole,
        confirmedCount,
        interestCount,
        workshopStarted
      ),
      subtitle: formatCommunityCardSubtitle(
        viewerRole,
        confirmedCount,
        interestCount,
        workshopStarted
      ),
    });
  }, [
    loading,
    error,
    viewerRole,
    confirmedCount,
    interestCount,
    workshopStarted,
    onHeaderChange,
  ]);
  if (loading) return <p className={styles.helperText}>Loading…</p>;
  if (error) return <p className={styles.helperText}>{error}</p>;

  if (viewerRole === 'interest') {
    const lines = formatInterestedViewerLines(
      confirmedCount,
      interestCount,
      workshopStarted,
      true
    );
    return (
      <div className={styles.communityBody}>
        {lines.map((line) => (
          <p key={line} className={styles.communityText}>
            {line}
          </p>
        ))}
      </div>
    );
  }

  const rosterMessage = formatConfirmedRosterNames(
    rosterNames,
    namesHiddenCount,
    confirmedCount
  );
  const showInterestSection = !workshopStarted && interestCount > 0;

  return (
    <div className={styles.communityBody}>
      <div className={styles.rosterBlock}>
        <p className={styles.rosterLabel}>
          Who&apos;s confirmed and planning to attend ({confirmedCount})
        </p>
        {viewerShowsName ? (
          <p className={styles.rosterNames}>{rosterMessage}</p>
        ) : (
          <p className={styles.communityText}>
            Show your name in preferences to see who else is attending.
          </p>
        )}
      </div>

      {showInterestSection && (
        <div className={styles.rosterBlock}>
          <p className={styles.rosterLabel}>Who&apos;s still deciding ({interestCount})</p>
          <p className={styles.communityText}>{formatInterestListSummary(interestCount)}</p>
        </div>
      )}

      {!workshopStarted && (
        <p className={styles.helperText}>
          Phone and email lists will be available to confirmed members when the workshop starts
          {firstSession !== 'date TBD' ? ` (${firstSession})` : ''}. You can opt in under
          preferences.
        </p>
      )}
    </div>
  );
};

export default WorkshopCommunitySection;
